import type { Object3D } from 'three';
import { Vector3 } from 'three';
import { ref } from 'valtio';

import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';

import type {
  PlayerActorState,
  PlayerAssetLoadStatus,
  PlayerAssetStatusState,
  PlayerCameraControlsHandle,
  PlayerCameraState,
  PlayerDuration,
  PlayerFollowState,
  PlayerFollowTarget,
  PlayerMixer,
  PlayerPlaybackRange,
  PlayerResolvedAnimation,
  PlayerSettings,
  PlayerTransportState,
} from '../types';

type Deps = object;

type State = {
  // Global playback state such as isPlaying, frame, duration
  transport: PlayerTransportState;
  // Per-actor playback metadata such as visibility and frame offsets.
  actorsById: Record<string, PlayerActorState>;
  // Per-actor mesh/animation loading and error status.
  assetStatusByActorId: Record<string, PlayerAssetStatusState>;
  // Reactive revision counters for actor scenes stored off-state.
  actorSceneVersionByActorId: Record<string, number>;
  // Reactive revision counters for resolved animation assets stored off-state.
  resolvedAnimationVersionByActorId: Record<string, number>;
  // The actor currently targeted by shared player controls.
  activeActorId: string | null;
  // Camera follow target selection and change versioning.
  follow: PlayerFollowState;
  camera: PlayerCameraState;
};

const DEFAULT_DURATION: PlayerDuration = {
  seconds: 0,
  frames: 0,
};

function createInitialAssetStatus(): PlayerAssetStatusState {
  return {
    meshStatus: 'initial',
    animationStatus: 'initial',
    error: null,
  };
}

function createInitialState(): State {
  return {
    transport: {
      isPlaying: false,
      currentFrame: 0,
      duration: { ...DEFAULT_DURATION },
      playbackRange: null,
    },
    actorsById: {},
    assetStatusByActorId: {},
    actorSceneVersionByActorId: {},
    resolvedAnimationVersionByActorId: {},
    activeActorId: null,
    follow: {
      target: null,
      version: 0,
    },
    camera: {
      gizmoDragging: false,
      gizmoSnapshot: null,
      autoFitSuppressedUntil: 0,
    },
  };
}

export class PlayerStore extends BaseStore<State, Deps> {
  private playbackAccumulator = 0;
  private playbackActorId: string | undefined;
  private actorMixersById = new Map<string, PlayerMixer>();
  private actorDurationsById = new Map<string, PlayerDuration>();
  private actorScenesById = new Map<string, Object3D>();
  private resolvedAnimationsByActorId = new Map<
    string,
    PlayerResolvedAnimation
  >();

  constructor(deps: Deps) {
    super(createInitialState(), deps);
  }

  init() {}

  reset() {
    const initialState = createInitialState();

    this.state.transport = initialState.transport;
    this.state.actorsById = initialState.actorsById;
    this.state.assetStatusByActorId = initialState.assetStatusByActorId;
    this.state.actorSceneVersionByActorId =
      initialState.actorSceneVersionByActorId;
    this.state.resolvedAnimationVersionByActorId =
      initialState.resolvedAnimationVersionByActorId;
    this.state.activeActorId = initialState.activeActorId;
    this.state.follow = initialState.follow;
    this.state.camera = initialState.camera;
    this.playbackAccumulator = 0;
    this.playbackActorId = undefined;
    this.actorMixersById.clear();
    this.actorDurationsById.clear();
    this.actorScenesById.clear();
    this.resolvedAnimationsByActorId.clear();
  }

  destroy() {
    this.actorMixersById.clear();
    this.actorDurationsById.clear();
    this.actorScenesById.clear();
    this.resolvedAnimationsByActorId.clear();
    this.playbackAccumulator = 0;
    this.playbackActorId = undefined;
    super.destroy();
  }

  registerActor(actorId: string, mixer?: PlayerMixer) {
    if (!this.state.actorsById[actorId]) {
      this.state.actorsById[actorId] = {
        id: actorId,
        visible: true,
        weight: 1,
        frameOffset: 0,
      };
    }

    if (!this.state.assetStatusByActorId[actorId]) {
      this.state.assetStatusByActorId[actorId] = createInitialAssetStatus();
    }

    if (mixer) {
      this.setActorMixer(actorId, mixer);
    }

    if (!this.state.activeActorId) {
      this.state.activeActorId = actorId;
    }

    return this.state.actorsById[actorId];
  }

  unregisterActor(actorId: string) {
    if (!this.state.actorsById[actorId]) return;
    delete this.state.actorsById[actorId];
    delete this.state.assetStatusByActorId[actorId];
    delete this.state.actorSceneVersionByActorId[actorId];
    delete this.state.resolvedAnimationVersionByActorId[actorId];
    this.actorMixersById.delete(actorId);
    this.actorDurationsById.delete(actorId);
    this.resolvedAnimationsByActorId.delete(actorId);
    this.unregisterActorScene(actorId);
    this.syncTransportDurationFromActors();

    if (this.state.activeActorId === actorId) {
      this.state.activeActorId = Object.keys(this.state.actorsById)[0] ?? null;
    }
  }

  getActor(actorId: string) {
    return this.state.actorsById[actorId];
  }

  getActorAssetStatus(actorId: string) {
    return (
      this.state.assetStatusByActorId[actorId] ?? createInitialAssetStatus()
    );
  }

  getAssetStatuses() {
    return this.state.assetStatusByActorId;
  }

  getActorScene(actorId: string) {
    return this.actorScenesById.get(actorId) ?? null;
  }

  getResolvedAnimation(actorId: string) {
    return this.resolvedAnimationsByActorId.get(actorId) ?? null;
  }

  setActiveActor(actorId: string | null) {
    if (!actorId) {
      this.state.activeActorId = null;
      return;
    }
    this.registerActor(actorId);
    this.state.activeActorId = actorId;
  }

  setActorHeadBoneName(actorId: string, headBoneName?: string | null) {
    this.registerActor(actorId);
    const actor = this.state.actorsById[actorId]!;
    const trimmed = headBoneName?.trim();
    if (trimmed) {
      actor.headBoneName = trimmed;
    } else {
      delete actor.headBoneName;
    }
  }

  registerActorScene(actorId: string, scene: Object3D) {
    this.registerActor(actorId);

    if (this.actorScenesById.get(actorId) === scene) {
      return;
    }

    this.actorScenesById.set(actorId, scene);
    this.state.actorSceneVersionByActorId[actorId] =
      (this.state.actorSceneVersionByActorId[actorId] ?? 0) + 1;
    this.state.follow.version += 1;
  }

  unregisterActorScene(actorId: string) {
    if (!this.actorScenesById.has(actorId)) {
      return;
    }

    this.actorScenesById.delete(actorId);
    this.state.actorSceneVersionByActorId[actorId] =
      (this.state.actorSceneVersionByActorId[actorId] ?? 0) + 1;

    if (this.state.follow.target?.actorId === actorId) {
      this.state.follow.target = null;
    }

    this.state.follow.version += 1;
  }

  setFollowTarget(target: PlayerFollowTarget) {
    if (
      this.state.follow.target?.actorId === target?.actorId &&
      this.state.follow.target?.targetName === target?.targetName
    ) {
      return;
    }

    this.state.follow.target = target;
    this.state.follow.version += 1;
  }

  setGizmoDragging(dragging: boolean) {
    if (this.state.camera.gizmoDragging === dragging) {
      return;
    }

    this.state.camera.gizmoDragging = dragging;
  }

  saveGizmoCameraSnapshot(controls: PlayerCameraControlsHandle) {
    const position = controls.getPosition(new Vector3());
    const target = controls.getTarget(new Vector3());
    this.state.camera.gizmoSnapshot = {
      position: [position.x, position.y, position.z],
      target: [target.x, target.y, target.z],
    };
  }

  restoreGizmoCameraSnapshot(controls: PlayerCameraControlsHandle) {
    const snapshot = this.state.camera.gizmoSnapshot;
    if (!snapshot) {
      return;
    }

    controls.stop?.();
    controls.setLookAt(
      snapshot.position[0],
      snapshot.position[1],
      snapshot.position[2],
      snapshot.target[0],
      snapshot.target[1],
      snapshot.target[2],
      false,
    );
    controls.update(0);
    this.state.camera.gizmoSnapshot = null;
  }

  suppressCharacterAutoFitAfterGizmo(ms = 500) {
    this.state.camera.autoFitSuppressedUntil = performance.now() + ms;
  }

  isPlayerCharacterFitBlocked(): boolean {
    if (this.state.camera.gizmoDragging) {
      return true;
    }

    return performance.now() < this.state.camera.autoFitSuppressedUntil;
  }

  getResolvedFollowTarget(
    target: PlayerFollowTarget = this.state.follow.target,
  ) {
    const actorId = target?.actorId ?? this.state.activeActorId;
    if (!actorId) {
      return null;
    }

    const actorScene = this.actorScenesById.get(actorId);
    if (!actorScene) {
      return null;
    }

    const targetName = target?.targetName ?? 'pelvis';
    return actorScene.getObjectByName(targetName) ?? actorScene;
  }

  setActorMixer(actorId: string, mixer?: PlayerMixer) {
    this.registerActor(actorId);

    if (!mixer) {
      this.actorMixersById.delete(actorId);
      this.actorDurationsById.delete(actorId);
      this.syncTransportDurationFromActors();
      return;
    }

    this.actorMixersById.set(actorId, ref(mixer) as unknown as PlayerMixer);
    this.setActorDuration(actorId, mixer.duration);
    const transportFrame = this.state.transport.currentFrame;
    mixer.setFrame(this.getActorTransportFrame(actorId));
    this.state.transport.currentFrame = transportFrame;
  }

  setActorVisibility(actorId: string, visible: boolean) {
    const actor = this.getOrCreateActor(actorId);
    actor.visible = visible;
  }

  setActorWeight(actorId: string, weight: number) {
    const actor = this.getOrCreateActor(actorId);
    actor.weight = Math.max(0, weight);
  }

  setActorFrameOffset(actorId: string, frameOffset: number) {
    const actor = this.getOrCreateActor(actorId);
    actor.frameOffset = Math.round(frameOffset);
  }

  setActorMeshStatus(actorId: string, status: PlayerAssetLoadStatus) {
    const assetStatus = this.getOrCreateAssetStatus(actorId);
    assetStatus.meshStatus = status;

    if (status === 'pending') {
      assetStatus.error = null;
    }
  }

  setActorAnimationStatus(actorId: string, status: PlayerAssetLoadStatus) {
    const assetStatus = this.getOrCreateAssetStatus(actorId);
    assetStatus.animationStatus = status;

    if (status === 'pending') {
      assetStatus.error = null;
    }
  }

  setActorAssetError(actorId: string, error: Error | null) {
    const assetStatus = this.getOrCreateAssetStatus(actorId);
    assetStatus.error = error;
  }

  resetActorAssetStatus(actorId: string) {
    this.registerActor(actorId);
    this.state.assetStatusByActorId[actorId] = createInitialAssetStatus();
  }

  setResolvedAnimation(
    actorId: string,
    resolvedAnimation: PlayerResolvedAnimation,
  ) {
    this.registerActor(actorId);
    this.resolvedAnimationsByActorId.set(actorId, resolvedAnimation);
    this.state.resolvedAnimationVersionByActorId[actorId] =
      (this.state.resolvedAnimationVersionByActorId[actorId] ?? 0) + 1;
  }

  clearResolvedAnimation(actorId: string) {
    if (!this.resolvedAnimationsByActorId.has(actorId)) {
      return;
    }

    this.resolvedAnimationsByActorId.delete(actorId);
    this.state.resolvedAnimationVersionByActorId[actorId] =
      (this.state.resolvedAnimationVersionByActorId[actorId] ?? 0) + 1;
  }

  setCurrentFrame(frame: number) {
    this.state.transport.currentFrame = this.state.transport.playbackRange
      ? this.clampFrameToPlaybackRange(frame)
      : frame;
  }

  setDuration(duration: PlayerDuration) {
    this.state.transport.duration = duration;
    this.syncCurrentFrameToPlaybackRange();
  }

  setActorDuration(actorId: string, duration: PlayerDuration) {
    this.registerActor(actorId);
    this.actorDurationsById.set(actorId, { ...duration });
    this.syncTransportDurationFromActors();
  }

  setIsPlaying(isPlaying: boolean) {
    this.state.transport.isPlaying = isPlaying;
  }

  setPlaybackRange(playbackRange: PlayerPlaybackRange | null) {
    const nextRange = this.normalizePlaybackRangeInput(playbackRange);
    const currentRange = this.state.transport.playbackRange;

    if (
      currentRange?.startFrame === nextRange?.startFrame &&
      currentRange?.endFrame === nextRange?.endFrame
    ) {
      return;
    }

    this.state.transport.playbackRange = nextRange;
    this.playbackAccumulator = 0;
    this.syncCurrentFrameToPlaybackRange();
  }

  play(actorId?: string) {
    const mixers = this.getTargetMixers(actorId);
    if (mixers.length === 0) return;

    this.playbackActorId = actorId;
    this.playbackAccumulator = 0;
    this.syncTargetMixersToCurrentFrame(actorId);
    mixers.forEach((mixer) => mixer.play(true));
    this.setIsPlaying(true);
  }

  pause(actorId?: string) {
    const mixers = this.getTargetMixers(actorId);
    if (mixers.length === 0) return;

    mixers.forEach((mixer) => mixer.play(false));
    this.setIsPlaying(false);
    this.playbackAccumulator = 0;
  }

  toggle(actorId?: string) {
    if (this.state.transport.isPlaying) {
      this.pause(actorId);
      return;
    }
    this.play(actorId);
  }

  setFrame(frame: number, actorId?: string) {
    const mixers = this.getTargetMixers(actorId);
    if (mixers.length === 0) return;

    const transportFrame = this.normalizeTransportFrame(frame);
    this.playbackAccumulator = 0;
    this.setCurrentFrame(transportFrame);
    this.syncTargetMixersToCurrentFrame(actorId);
  }

  updatePlayback(delta: number, playbackSettings: PlayerSettings['playback']) {
    if (
      !this.state.transport.isPlaying ||
      playbackSettings.disabled ||
      this.state.transport.duration.frames <= 0
    ) {
      return;
    }

    const frameDelta = this.consumePlaybackFrameDelta(delta, playbackSettings);
    if (frameDelta === 0) {
      return;
    }

    const nextFrame = this.resolvePlaybackFrame(
      this.state.transport.currentFrame + frameDelta,
      playbackSettings.loop,
    );

    this.state.transport.currentFrame = nextFrame;
    this.syncTargetMixersToCurrentFrame(this.playbackActorId);
  }

  frameJump(numFrames: number, actorId?: string) {
    this.setFrame(
      Math.max(this.state.transport.currentFrame + numFrames, 0),
      actorId,
    );
  }

  playOrPause(playState?: boolean, actorId?: string) {
    if (playState === undefined) {
      this.toggle(actorId);
      return;
    }

    if (playState) {
      this.play(actorId);
      return;
    }

    this.pause(actorId);
  }

  private getTargetMixers(actorId?: string): PlayerMixer[] {
    if (actorId) {
      const mixer = this.actorMixersById.get(actorId);
      return mixer ? [mixer] : [];
    }

    return Array.from(this.actorMixersById.values());
  }

  // Converts elapsed render time into whole frame steps using playback settings.
  private consumePlaybackFrameDelta(
    delta: number,
    playbackSettings: PlayerSettings['playback'],
  ) {
    const frameStepSize = Math.max(
      1,
      Math.round(playbackSettings.frameStepSize || 1),
    );
    const stepTime = frameStepSize / (playbackSettings.frameRate || 24);
    this.playbackAccumulator += delta * (playbackSettings.playbackSpeed || 1);

    let frameDelta = 0;
    while (this.playbackAccumulator >= stepTime) {
      frameDelta += frameStepSize;
      this.playbackAccumulator -= stepTime;
    }

    return frameDelta;
  }

  private normalizePlaybackRangeInput(
    playbackRange: PlayerPlaybackRange | null,
  ): PlayerPlaybackRange | null {
    if (!playbackRange) {
      return null;
    }

    const startFrame = Number.isFinite(playbackRange.startFrame)
      ? Math.max(0, Math.round(playbackRange.startFrame))
      : 0;
    const endFrame = Number.isFinite(playbackRange.endFrame)
      ? Math.max(0, Math.round(playbackRange.endFrame))
      : startFrame;

    return {
      startFrame: Math.min(startFrame, endFrame),
      endFrame: Math.max(startFrame, endFrame),
    };
  }

  private getEffectivePlaybackRange(): PlayerPlaybackRange | null {
    const playbackRange = this.state.transport.playbackRange;
    if (!playbackRange) {
      return null;
    }

    const frameCount = this.state.transport.duration.frames;
    if (frameCount <= 0) {
      return playbackRange;
    }

    const maxFrame = Math.max(frameCount - 1, 0);
    return {
      startFrame: Math.min(playbackRange.startFrame, maxFrame),
      endFrame: Math.min(playbackRange.endFrame, maxFrame),
    };
  }

  private clampFrameToPlaybackRange(frame: number) {
    const playbackRange = this.getEffectivePlaybackRange();
    const fallbackFrame = playbackRange?.startFrame ?? 0;
    const safeFrame = Number.isFinite(frame) ? frame : fallbackFrame;

    if (!playbackRange) {
      return Math.max(safeFrame, 0);
    }

    return Math.min(
      Math.max(safeFrame, playbackRange.startFrame),
      playbackRange.endFrame,
    );
  }

  private wrapFrameInPlaybackRange(frame: number) {
    const playbackRange = this.getEffectivePlaybackRange();
    if (!playbackRange) {
      return this.normalizeTransportFrame(frame);
    }

    const frameCount = playbackRange.endFrame - playbackRange.startFrame + 1;
    if (frameCount <= 0) {
      return playbackRange.startFrame;
    }

    return (
      playbackRange.startFrame +
      ((((frame - playbackRange.startFrame) % frameCount) + frameCount) %
        frameCount)
    );
  }

  private syncCurrentFrameToPlaybackRange() {
    if (!this.state.transport.playbackRange) {
      return;
    }

    const nextFrame = this.clampFrameToPlaybackRange(
      this.state.transport.currentFrame,
    );
    if (this.state.transport.currentFrame === nextFrame) {
      return;
    }

    this.state.transport.currentFrame = nextFrame;
    this.playbackAccumulator = 0;
    this.syncTargetMixersToCurrentFrame();
  }

  private stopPlaybackAtBoundary() {
    this.state.transport.isPlaying = false;
    this.getTargetMixers(this.playbackActorId).forEach((mixer) =>
      mixer.play(false),
    );
  }

  // Mirrors frame mixer wrapping so UI transport state stays in sync.
  private normalizeTransportFrame(frame: number) {
    if (!Number.isFinite(frame)) {
      return this.state.transport.playbackRange
        ? this.clampFrameToPlaybackRange(frame)
        : 0;
    }

    if (this.state.transport.playbackRange) {
      return this.clampFrameToPlaybackRange(frame);
    }

    const frameCount = this.state.transport.duration.frames;
    if (frameCount <= 0) {
      return Math.max(frame, 0);
    }

    return ((frame % frameCount) + frameCount) % frameCount;
  }

  // Applies loop or end-of-clip behavior to the next shared playback frame.
  private resolvePlaybackFrame(frame: number, loop: boolean) {
    const playbackRange = this.getEffectivePlaybackRange();

    if (playbackRange) {
      if (loop) {
        return this.wrapFrameInPlaybackRange(frame);
      }

      if (frame <= playbackRange.endFrame) {
        return Math.max(frame, playbackRange.startFrame);
      }

      this.stopPlaybackAtBoundary();
      return playbackRange.endFrame;
    }

    const frameCount = this.state.transport.duration.frames;
    if (loop) {
      return ((frame % frameCount) + frameCount) % frameCount;
    }

    if (frame < frameCount) {
      return frame;
    }

    this.stopPlaybackAtBoundary();
    return Math.max(frameCount - 1, 0);
  }

  // Pushes the shared transport frame to one actor mixer or all actor mixers.
  private syncTargetMixersToCurrentFrame(actorId?: string) {
    const transportFrame = this.state.transport.currentFrame;

    if (actorId) {
      const mixer = this.actorMixersById.get(actorId);
      if (mixer) {
        mixer.setFrame(this.getActorTransportFrame(actorId));
      }
      this.state.transport.currentFrame = transportFrame;
      return;
    }

    this.actorMixersById.forEach((mixer, nextActorId) => {
      mixer.setFrame(this.getActorTransportFrame(nextActorId));
    });
    this.state.transport.currentFrame = transportFrame;
  }

  // Returns the shared frame adjusted by the actor's configured frame offset.
  // When actors have different motion lengths, shorter motions hold their last frame until the longest clip ends
  private getActorTransportFrame(actorId: string) {
    const frameOffset = this.state.actorsById[actorId]?.frameOffset ?? 0;
    const transportFrame = this.state.transport.currentFrame + frameOffset;

    if (!this.shouldHoldShorterActorsAtEnd()) {
      return transportFrame;
    }

    const actorFrames = this.actorDurationsById.get(actorId)?.frames ?? 0;
    if (actorFrames <= 0) {
      return transportFrame;
    }

    return Math.min(Math.max(transportFrame, 0), actorFrames - 1);
  }

  private shouldHoldShorterActorsAtEnd() {
    if (this.actorDurationsById.size <= 1) {
      return false;
    }

    const frameCounts = Array.from(this.actorDurationsById.values())
      .map((duration) => duration.frames)
      .filter((frames) => frames > 0);

    if (frameCounts.length <= 1) {
      return false;
    }

    return Math.min(...frameCounts) !== Math.max(...frameCounts);
  }

  // Derives shared transport duration from the longest registered actor animation.
  private syncTransportDurationFromActors() {
    const durations = Array.from(this.actorDurationsById.values());
    if (durations.length === 0) {
      this.state.transport.duration = { ...DEFAULT_DURATION };
      this.syncCurrentFrameToPlaybackRange();
      return;
    }

    const longestDuration = durations.reduce<PlayerDuration>(
      (longest, duration) => {
        if (duration.frames > longest.frames) {
          return duration;
        }

        if (
          duration.frames === longest.frames &&
          duration.seconds > longest.seconds
        ) {
          return duration;
        }

        return longest;
      },
      DEFAULT_DURATION,
    );

    this.state.transport.duration = { ...longestDuration };
    this.syncCurrentFrameToPlaybackRange();
  }

  private getOrCreateActor(actorId: string): PlayerActorState {
    if (this.state.actorsById[actorId]) {
      return this.state.actorsById[actorId];
    }

    return this.registerActor(actorId);
  }

  private getOrCreateAssetStatus(actorId: string): PlayerAssetStatusState {
    this.registerActor(actorId);
    return this.state.assetStatusByActorId[actorId];
  }
}

export const {
  useStore: usePlayerStore,
  useStoreApi: usePlayerStoreApi,
  Provider: PlayerStoreProvider,
  withProvider: withPlayerStoreProvider,
  Context: PlayerStoreContext,
} = createStoreProvider({
  Store: PlayerStore,
  displayName: 'PlayerStore',
});
